import React, { FC, useCallback, useEffect, useState } from "react";
import { useRouter } from "next/router";
import { observer } from "mobx-react";
import _omit from "lodash/omit";
import Title from "ui/Title/Title";
import Modal from "ui/Modal/Modal";
import Button from "ui/Button/Button";
import RegionAutocomplete from "common/RegionAutocomplete/RegionAutocomplete";
import { useStore } from "store";
import { gtm } from "helpers/analytics";
import { isURLRegional, withTrailSlash } from "helpers/url";
import { cn } from "helpers/style";
import {
  DEFAULT_REGION_CODE,
  REGION_SUPPRESSED_ROUTES,
} from "constants/variables";
import styles from "./RegionModal.module.scss";

type Props = {
  onClose?: () => void;
};

const RegionModal: FC<Props> = ({ onClose }: Props) => {
  const { location, notifications } = useStore();
  const router = useRouter();
  const [isMobileFullScreen, setIsMobileFullScreen] = useState<boolean>(false);
  const [region, setRegion] = useState<Entity.Region>(location.region);
  const onCloseHandler = useCallback(
    (withoutEvent?: boolean) => {
      !withoutEvent && gtm.locationModalClose(location.region.name);
      onClose && onClose();
    },
    [location.region.name, onClose]
  );

  // при первом появлении модалки
  // сохраняем предложенный регион
  // сделанно потому что модалка не показывается если регион уже выбран.
  useEffect(() => {
    gtm.locationModalOpen(location.region.name);
    const setRegion = async () => {
      if (!location.getRegionCookieClient()) {
        const realRegion = await getRealRegionByGeoSuggestion(region);
        realRegion && (location.region = realRegion);
      }
    };

    setRegion();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /*const updateProfile = async region => {
    const updateProfile = await user.updateProfile({
      profile: {
        regionID: region.id,
        addresses:
          //чистим мусор и добавляем выбранного адреса
          user.profile?.addresses?.map((address, index) => {
            const { __typename, ...copyAdr } = address;
            if (index === 0) {
              copyAdr.isSelected = true;
            }
            return copyAdr;
          }) || [],
      },
    });
    if (updateProfile.errors) {
      notifications.show({
        type: "toast",
        props: {
          title: "Не удалось обновить профиль",
          children: "Попробуйте позже",
        },
      });
    }
  };*/

  const getRealRegionByGeoSuggestion = useCallback(async geoObject => {
    const realRegion = await location.getRegionByGeoObject(geoObject);
    if (!realRegion) {
      notifications.show({
        type: "toast",
        props: {
          title: "Ошибка при получении региона",
          children: `Не удалось получить данные о регионе для ${geoObject?.name}(${geoObject?.code})`,
        },
      });
    }
    return realRegion;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const nextStep = useCallback(async () => {
    const realRegion = await getRealRegionByGeoSuggestion(region);
    if (realRegion) {
      if (location.regionID !== String(realRegion.id).toLowerCase()) {
        gtm.locationModalChange(realRegion.name);
      } else {
        gtm.locationModalConfirm(location.region.name);
      }

      location.region = realRegion;

      /* suppress for products */
      if (
        router.asPath.includes(".html") ||
        REGION_SUPPRESSED_ROUTES.includes(router.pathname)
      ) {
        onCloseHandler(true);
        return;
      }

      const isDefaultRegion =
        String(realRegion?.id).toLowerCase() ===
        DEFAULT_REGION_CODE.toLowerCase();
      // если регион Москва удаляем регион из URL
      const pathnameHasRegion = isURLRegional(router.asPath);
      let newPathName, newAsPathname;
      /* todo: let query = _omit(router.query, [PAGE_NUMBER_LITERAL]); */
      let query = router.query;
      const currentPathname = router.pathname;

      if (pathnameHasRegion) {
        newPathName = currentPathname;

        const asPathWithoutRegion = router.asPath.split("r_")[0];
        newAsPathname = isDefaultRegion
          ? asPathWithoutRegion
          : `${asPathWithoutRegion}r_${realRegion.id}/`;
        query = isDefaultRegion ? _omit(query, "region") : query;
      } else {
        newPathName = currentPathname;
        const asPathname = router.asPath.includes("?")
          ? router.asPath.split("?")[0]
          : router.asPath;
        newAsPathname = isDefaultRegion
          ? asPathname
          : `${withTrailSlash(asPathname)}r_${realRegion.id}/`;
      }

      const href = {
        pathname: newPathName,
        query,
      };

      /* Collect all [value]-like parts as keys for omit from query */
      const replaced = [...newPathName.matchAll(/([^[]+(?=]))/g)].map(
        i => i[0]
      );

      const as = {
        pathname: newAsPathname,
        query: {
          ..._omit(query, replaced.concat(["region", "category"])),
        },
      };

      if (!isDefaultRegion) {
        href.query.region = realRegion.id as string;
      } else {
        delete href.query.region;
      }

      await router.push(href, as, { scroll: false });

      /* todo: enable address request later */
      /*if (user?.profile) {
        if (user.profile?.regionID !== realRegion.id) {
          await updateProfile(realRegion);
        }
        location.requestAddress();
      }*/
    }
    onCloseHandler(true);
  }, [getRealRegionByGeoSuggestion, region, onCloseHandler, location, router]);

  /*const skipStep = () => {
    if (user?.profile) {
      location.requestAddress();
    }
    onClose && onClose();
  };*/

  const updateRegion = region => {
    setRegion(region);
  };

  return (
    <div className={styles.regionModal}>
      <Modal onClose={onCloseHandler}>
        <div className={styles.regionModalImage} />
        <div className={styles.regionModalForm}>
          <Title size="h2" className={styles.regionModalFormTitle}>
            Выберите ваш регион
          </Title>

          <div className={styles.regionModalFormHint}>
            От этого зависят <span>цены</span> и <span>ассортимент</span>{" "}
            товаров
          </div>

          <div
            className={cn([
              styles.regionModalFormInput,
              isMobileFullScreen && styles.mobileFull,
            ])}
          >
            <RegionAutocomplete
              region={region}
              onChange={updateRegion}
              onFocus={() => setIsMobileFullScreen(true)}
              onBlur={() => setIsMobileFullScreen(false)}
              className={cn([
                styles.regionModalFormInputAutocomplete,
                isMobileFullScreen && styles.mobileFull,
              ])}
              classNameTextField={styles.regionModalFormInputTextField}
            />
          </div>

          <div className={styles.regionModalFormActions}>
            <Button
              onClick={nextStep}
              disabled={!region}
              className={styles.regionModalFormActionsButton}
              orange
            >
              Подтвердить
            </Button>
            {/*<Button onClick={skipStep} outlined white>
              Пропустить
            </Button> */}
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default observer(RegionModal);
